import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { INVENTORY_COST_CHANGE } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import { SelectDropdown } from '../../components/SelectDropdown';
import { ChevronLeftIcon, ChevronRightIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { arrayHasItems } from '../../helpers/common';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';
import { isValidQuarterParam, parseQuarterParam, serializeQuarter } from '../../hooks/dateParamHelpers';

const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DATE_TYPES = {
  ALL_TIME: 'all_time',
  RANGE: 'range',
  QUARTER: 'quarter',
};

const quarters = [
  { label: 'Q1', startMonth: 0, endMonth: 2 },
  { label: 'Q2', startMonth: 3, endMonth: 5 },
  { label: 'Q3', startMonth: 6, endMonth: 8 },
  { label: 'Q4', startMonth: 9, endMonth: 11 },
];

function getQuarterStartEnd(year, qIndex) {
  const { startMonth, endMonth } = quarters[qIndex];
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, endMonth + 1, 0);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getAllTimeRange() {
  const start = new Date(2020, 0, 1); // 2020-01-01
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function formatPrettyDate(ymd) {
  if (!ymd) return '';
  const s = String(ymd).trim();
  const parts = s.split('-').map((x) => String(x).trim());
  if (parts.length !== 3) return s;
  const [yyyy, mm, dd] = parts;
  const mIdx = Number(mm) - 1;
  const dStr = String(dd).padStart(2, '0');
  const mStr = monthShort[mIdx] || mm;
  return `${dStr} ${mStr}, ${yyyy}`;
}

function formatCurrency(value) {
  const n = Number(value);
  if (value === null || value === undefined || value === '') return '';
  if (Number.isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

const InventoryCostChangeReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['inventory-cost-change'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['inventory-cost-change'] || [];
  const [isOpenSettings, setIsOpenSettings] = useState(false);

  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const now = new Date();
  const defaultRange = getAllTimeRange();
  const defaultQuarter = { quarterYear: now.getFullYear(), quarterIndex: Math.floor(now.getMonth() / 3) };
  const [dateType, setDateType] = useUrlParam(
    'dateType',
    DATE_TYPES.ALL_TIME,
    (value) => Object.values(DATE_TYPES).includes(value),
  );
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultRange.startDate, defaultRange.endDate);
  const [quarterParam, setQuarterParam] = useUrlParam('quarter', serializeQuarter(defaultQuarter), isValidQuarterParam);
  const quarter = useMemo(
    () => parseQuarterParam(quarterParam, defaultQuarter),
    [quarterParam, defaultQuarter.quarterYear, defaultQuarter.quarterIndex],
  );
  const { quarterYear, quarterIndex } = quarter;
  const [isQuarterPickerOpen, setIsQuarterPickerOpen] = useState(false);

  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(String(search || '').trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const effectiveRange = useMemo(() => {
    if (dateType === DATE_TYPES.ALL_TIME) return getAllTimeRange();
    if (dateType === DATE_TYPES.RANGE) return { startDate, endDate };
    return getQuarterStartEnd(quarterYear, quarterIndex);
  }, [dateType, startDate, endDate, quarterYear, quarterIndex]);

  useEffect(() => {
    fetchReportMetadata();
  }, []);

  // Load email settings + history on mount
  useEffect(() => {
    dispatch(getReportEmailSettings('inventory-cost-change'));
    dispatch(getReportSendHistory('inventory-cost-change'));
  }, [dispatch]);

  useEffect(() => {
    if (!initialReady) return;
    if (effectiveRange.startDate && effectiveRange.endDate) fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReady, effectiveRange.startDate, effectiveRange.endDate, currentPage, debouncedSearch]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${INVENTORY_COST_CHANGE}/metadata`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report metadata');
      const data = await response.json();
      setColumns(data.columns || []);
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const fetchReportData = async () => {
    if (!effectiveRange.startDate || !effectiveRange.endDate) return;
    const { signal, requestId } = beginRequest();

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
      });
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      const response = await fetch(`/api/v1/reports/${INVENTORY_COST_CHANGE}?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch report data');
      }

      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1);
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSuccessSubmit = async (data) => {
    const payload = {
      emailRecipients: data?.emailRecipients || [],
      scheduleType: data?.schedule || null,
      dayOfMonth: data?.dayOfMonth ?? null,
      slackChannelId: data?.slackChannelId ?? null,
      dayOfWeek: data?.dayOfWeek ?? null,
      timeOfDay: data?.timeOfDay ?? null,
      emailMessage: data?.emailMessage ?? null,
      emailSendPreviewTo: data?.emailSendPreviewTo ?? null,
    };

    try {
      await dispatch(updateReportEmailSettings({ reportType: 'inventory-cost-change', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (data) => {
    try {
      const payload = {
        email: data?.emailSendPreviewTo,
        emailMessage: data?.emailMessage || '',
        dateType: DATE_TYPES.ALL_TIME,
        scheduleType: data?.type || null,
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        search: debouncedSearch || null,
      }; 
      await dispatch(sendReportPreviewEmail({ reportType: 'inventory-cost-change', ...payload })).unwrap();
      await dispatch(getReportSendHistory('inventory-cost-change')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (data) => {
    try {
      const payload = {
        slackChannelId: data?.slackChannelId,
        emailMessage: data?.emailMessage || '',
        dateType: DATE_TYPES.ALL_TIME,
        scheduleType: data?.type || null,
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        search: debouncedSearch || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'inventory-cost-change', ...payload })).unwrap();
      await dispatch(getReportSendHistory('inventory-cost-change')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const handleExportCSV = async () => {
    if (!effectiveRange.startDate || !effectiveRange.endDate) throw new Error('Please select a date range first');
    const params = new URLSearchParams({ startDate: effectiveRange.startDate, endDate: effectiveRange.endDate });
    if (debouncedSearch) params.append('search', debouncedSearch);
    const response = await fetch(`/api/v1/reports/${INVENTORY_COST_CHANGE}/export?${params}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `${INVENTORY_COST_CHANGE}-report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  const filtersComponent = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search SKU or PO #"
          className="w-40 sm:w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      <div className="flex flex-row items-center gap-2">
        <SelectDropdown
          selectClassname="!w-36 !bg-white"
          options={[
            { label: 'All Time', value: DATE_TYPES.ALL_TIME },
            { label: 'Date Range', value: DATE_TYPES.RANGE },
            { label: 'Quarter', value: DATE_TYPES.QUARTER },
          ]}
          value={dateType}
          onChange={(value) => {
            setDateType(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {dateType === DATE_TYPES.RANGE && (
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          label="Date Range"
          error={error && error.includes('date') ? error : null}
        />
      )}

      {dateType === DATE_TYPES.QUARTER && (
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const q = quarterIndex - 1;
              if (q < 0) {
                setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex: 3 }));
              } else {
                setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
              }
              setCurrentPage(1);
            }}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous quarter"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[110px]"
            onClick={() => setIsQuarterPickerOpen((v) => !v)}
            title="Pick quarter and year"
          >
            {`${quarters[quarterIndex].label} ${quarterYear}`}
          </button>
          <button
            type="button"
            onClick={() => {
              const q = quarterIndex + 1;
              if (q > 3) {
                setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex: 0 }));
              } else {
                setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
              }
              setCurrentPage(1);
            }}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next quarter"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>

          {isQuarterPickerOpen && (
            <div className="absolute top-full mt-2 z-10 bg-white border border-gray-200 rounded-md shadow w-64 p-3">
              <div className="flex items-center justify-between mb-3">
                <button className="px-2 py-1 border rounded text-sm" onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex }))}>
                  {quarterYear - 1}
                </button>
                <div className="font-medium">{quarterYear}</div>
                <button className="px-2 py-1 border rounded text-sm" onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex }))}>
                  {quarterYear + 1}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quarters.map((q, idx) => (
                  <button
                    key={q.label}
                    className={`px-2 py-2 border rounded text-sm ${idx === quarterIndex ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
                    onClick={() => {
                      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: idx }));
                      setIsQuarterPickerOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {q.label} {quarterYear}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Cog8ToothIcon
          className="h-5 w-5 text-primary cursor-pointer"
          onClick={() => {
            setIsOpenSettings(true);
          }}
        />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${INVENTORY_COST_CHANGE}-report`}
      onExport={handleExportCSV}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const renderTableRows = () => {
    return (reportData || []).map((row, index) => (
      <tr key={`${row?.po_id || 'po'}-${row?.product_id || 'prod'}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {formatPrettyDate(row.date)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row.sku ? (
            <a
              href={`${ROUTES.CATALOG}?search=${encodeURIComponent(row.sku)}`}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.sku}
            </a>
          ) : (
            ''
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.landed_cost)}</td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(row.landed_cost_change || 0) < 0
              ? 'text-green-600'
              : Number(row.landed_cost_change || 0) > 0
                ? 'text-red-600'
                : 'text-gray-900'
          }`}
        >
          {formatCurrency(row.landed_cost_change)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.new_landed_cost)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.weighted_cost)}</td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(row.weighted_cost_change || 0) < 0
              ? 'text-green-600'
              : Number(row.weighted_cost_change || 0) > 0
                ? 'text-red-600'
                : 'text-gray-900'
          }`}
        >
          {formatCurrency(row.weighted_cost_change)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.new_weighted_cost)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row.po_number ? (
            <a
              href={`${ROUTES.FINANCE}?search=${encodeURIComponent(`${row.po_number}`)}`}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.po_number}
            </a>
          ) : (
            ''
          )}
        </td>
      </tr>
    ));
  };
 
  const groupedHeaders = useMemo(() => {
    if (!arrayHasItems(columns)) return null;
    const result = [];
    let i = 0;
    while (i < columns.length) {
      const col = columns[i];
      if (col.key === 'landed_cost') {
        result.push({ label: 'Landed Cost', colSpan: 3 });
        i += 3;
      } else if (col.key === 'weighted_cost') {
        result.push({ label: 'Weighted Cost', colSpan: 3 });
        i += 3;
      } else {
        result.push({ label: '', colSpan: 1 });
        i += 1;
      }
    }
    return result;
  }, [columns]);

  return (
    <>
      <ReportTemplate
        reportId={INVENTORY_COST_CHANGE}
        description="Cost history changes for Landed Cost / Weighted Cost by SKU and PO"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={reportData}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No cost changes found for the selected date range."
        groupedHeaders={groupedHeaders}
        cronType={emailSettings?.cronType || null}
      >
        {renderTableRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'inventory-cost-change'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="inventory-cost-change"
      />
    </>
  );
};

export default InventoryCostChangeReport;
